import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
  Stack,
  TextField,
  Snackbar,
  Alert,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import api from "../../utils/api";

export default function AdminSubscriptionsPage() {
  const [rows, setRows] = useState([]);
  const [days, setDays] = useState(7);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  // feedback
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // confirmation modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState(""); // expire | suspend | unsuspend
  const [confirmUser, setConfirmUser] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState("");

  /* =============================
     LOAD DATA
  ============================== */
  const loadData = async () => {
    try {
      const { data } = await api.get("/admin/subscriptions");
      setRows(data);
    } catch (err) {
      setError("Failed to load subscriptions");
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  /* =============================
     CONFIRMATION HANDLING
  ============================== */
  const openConfirm = (type, user, message) => {
    setConfirmType(type);
    setConfirmUser(user);
    setConfirmMessage(message);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmType("");
    setConfirmUser(null);
    setConfirmMessage("");
  };

  const handleConfirm = async () => {
    if (!confirmUser) {
      setError("No user selected");
      closeConfirm();
      return;
    }

    try {
      if (confirmType === "expire") {
        await api.post("/admin/subscriptions/expire", {
          user_id: confirmUser,
        });
        setSuccess("Subscription expired");
      }

      if (confirmType === "suspend") {
        await api.post("/admin/users/suspend", {
          user_id: confirmUser,
        });
        setSuccess("User suspended and access revoked");
      }

      if (confirmType === "unsuspend") {
        await api.post("/admin/users/unsuspend", {
          user_id: confirmUser,
        });
        setSuccess("User unsuspended (no subscription restored)");
      }

      closeConfirm();
      loadData();
    } catch (err) {
      setError(
        err.response?.data?.message || "Action failed. Please try again."
      );
      closeConfirm();
    }
  };

  /* =============================
     SUBSCRIPTION ACTIONS
  ============================== */
  const extendSubscription = async (user_id) => {
    try {
      await api.post("/admin/subscriptions/extend", {
        user_id,
        extra_days: Number(days),
      });
      setSuccess("Subscription extended");
      loadData();
    } catch {
      setError("Failed to extend subscription");
    }
  };

  /* =============================
     FILTERING
  ============================== */
  const filtered = rows.filter((r) => {
    const matchSearch =
      r.email.toLowerCase().includes(search.toLowerCase()) ||
      r.full_name.toLowerCase().includes(search.toLowerCase());

    const status = r.is_suspended
      ? "suspended"
      : r.status || "expired";

    const matchFilter = filter === "all" || status === filter;

    return matchSearch && matchFilter;
  });

  /* =============================
     RENDER
  ============================== */
  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        💼 Subscription & Access Management
      </Typography>

      {/* FILTERS */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Search user"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <TextField
          select
          label="Status"
          size="small"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ width: 160 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="expired">Expired</MenuItem>
          <MenuItem value="suspended">Suspended</MenuItem>
        </TextField>
      </Stack>

      <Paper sx={{ p: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell>Days Left</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filtered.map((r) => {
              const statusLabel = r.is_suspended
                ? "suspended"
                : r.status || "expired";

              return (
                <TableRow key={r.user_id}>
                  <TableCell>{r.full_name}</TableCell>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>{r.plan || "-"}</TableCell>

                  <TableCell>
                    <Chip
                      label={statusLabel}
                      color={
                        statusLabel === "active"
                          ? "success"
                          : statusLabel === "suspended"
                          ? "warning"
                          : "error"
                      }
                      size="small"
                    />
                  </TableCell>

                  <TableCell>
                    {r.expiry_date
                      ? new Date(r.expiry_date).toLocaleDateString()
                      : "-"}
                  </TableCell>

                  <TableCell>{r.days_remaining ?? 0}</TableCell>

                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {/* EXTEND */}
                      <TextField
                        type="number"
                        size="small"
                        value={days}
                        onChange={(e) => setDays(e.target.value)}
                        sx={{ width: 70 }}
                        disabled={statusLabel !== "active"}
                      />
                      <Button
                        size="small"
                        onClick={() => extendSubscription(r.user_id)}
                        disabled={statusLabel !== "active"}
                      >
                        Extend
                      </Button>

                      {/* EXPIRE */}
                      <Button
                        size="small"
                        color="error"
                        onClick={() =>
                          openConfirm(
                            "expire",
                            r.user_id,
                            "Expire this subscription? The user will lose subscription access but can still log in."
                          )
                        }
                      >
                        Expire
                      </Button>

                      {/* SUSPEND / UNSUSPEND */}
                      {r.is_suspended ? (
                        <Button
                          size="small"
                          color="success"
                          onClick={() =>
                            openConfirm(
                              "unsuspend",
                              r.user_id,
                              "Unsuspend this user? Account access will be restored, but subscription time will NOT be restored."
                            )
                          }
                        >
                          Unsuspend
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          color="warning"
                          onClick={() =>
                            openConfirm(
                              "suspend",
                              r.user_id,
                              "Suspend this user? This will immediately block access regardless of subscription."
                            )
                          }
                        >
                          Suspend
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* CONFIRM MODAL */}
      <Dialog open={confirmOpen} onClose={closeConfirm}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>{confirmMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* SUCCESS */}
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess("")}
      >
        <Alert severity="success">{success}</Alert>
      </Snackbar>

      {/* ERROR */}
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError("")}
      >
        <Alert severity="error">{error}</Alert>
      </Snackbar>
    </Box>
  );
}
